export enum ActionType {
  Added = 'added',
  Removed = 'removed',
  Changed = 'changed',
}

type Diff =
  | { actionType: ActionType.Added; key: string; value: any }
  | { actionType: ActionType.Removed; key: string; value: any }
  | { actionType: ActionType.Changed; key: string; from: any; to: any };

function isObject(val: any): val is Record<string, any> {
  return val && typeof val === 'object' && !Array.isArray(val);
}

function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, i) => deepEqual(val, b[i]));
  }

  if (isObject(a) && isObject(b)) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every((key) => deepEqual(a[key], b[key]));
  }

  return false;
}

export const diffObjects = (obj1: any, obj2: any, parentKey = ''): Diff[] => {
  const diffs: Diff[] = [];

  if (!obj1 || !obj2) {
    return diffs;
  }

  const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

  // @ts-ignore
  for (const key of allKeys) {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;
    const val1 = obj1?.[key];
    const val2 = obj2?.[key];

    if (!(key in obj2)) {
      diffs.push({ actionType: ActionType.Removed, key: fullKey, value: val1 });
    } else if (!(key in obj1)) {
      diffs.push({ actionType: ActionType.Added, key: fullKey, value: val2 });
    } else if (!deepEqual(val1, val2)) {
      if (isObject(val1) && isObject(val2)) {
        diffs.push(...diffObjects(val1, val2, val1.name || fullKey));
      } else if (Array.isArray(val1) && Array.isArray(val2)) {
        if (!deepEqual(val1, val2)) {
          diffs.push({ actionType: ActionType.Changed, key: key, from: val1, to: val2 });
        }
      } else {
        diffs.push({ actionType: ActionType.Changed, key: fullKey, from: val1, to: val2 });
      }
    }
  }

  return diffs;
};
